import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["input", "results", "option"]

    connect() {
        this.createCustomUI()
        this.boundClose = this.close.bind(this)
        document.addEventListener("click", this.boundClose)
    }

    disconnect() {
        if (this.wrapper) this.wrapper.remove()
        // results lives on <body>, remove it too. Otherwise Turbo
        // navigations would leave orphan dropdowns behind.
        if (this.results && this.results.parentNode) this.results.remove()
        if (this._boundReposition) {
            window.removeEventListener("scroll", this._boundReposition, true)
            window.removeEventListener("resize", this._boundReposition)
            this._boundReposition = null
        }
        this.element.style.display = ""
        document.removeEventListener("click", this.boundClose)
    }

    createCustomUI() {
        // Hide original select
        this.element.style.display = "none"

        // Create wrapper
        this.wrapper = document.createElement("div")
        this.wrapper.className = "relative searchable-select-wrapper"

        // Create input
        this.input = document.createElement("input")
        this.input.type = "text"
        this.input.className = "form-control w-full"
        this.input.placeholder = "Search..."
        this.input.setAttribute("autocomplete", "off")

        // Set initial value if something is selected
        const selectedOption = this.element.options[this.element.selectedIndex]
        if (selectedOption && selectedOption.value) {
            this.input.value = selectedOption.text
            // Native browser tooltip — hover the input to see the full
            // value when it's truncated visually (long location paths,
            // product names with sizes, etc).
            this.input.title = selectedOption.text
        } else if (this.element.getAttribute('prompt')) {
            this.input.placeholder = this.element.getAttribute('prompt')
        }

        // Event listeners for input
        this.input.addEventListener("input", this.filter.bind(this))
        this.input.addEventListener("focus", this.open.bind(this))
        this.input.addEventListener("keydown", this.navigate.bind(this))

        // Results <ul>. Rendered as position: fixed and appended to
        // <body> at open time so it escapes any overflow-clipping
        // ancestor (e.g. a table wrapped in overflow-x-auto, or a
        // modal with overflow:hidden). positionResults() computes
        // its coords from the input's bounding rect.
        this.results = document.createElement("ul")
        this.results.className = "z-50 bg-surface border border-med rounded max-h-60 overflow-y-auto hidden shadow-lg searchable-select-results"
        this.results.style.position        = "fixed"
        this.results.style.zIndex          = "1000"
        this.results.style.backgroundColor = "var(--bg-surface)"
        this.results.style.borderColor     = "var(--border-light)"
        this.results.style.margin          = "0"
        this.results.style.padding         = "0"
        this.results.style.listStyle       = "none"

        // Populate options. Empty-value options (e.g. "-- Create new --")
        // are KEPT and rendered subtly so the user can still fall back to
        // them via search ("create", "new", etc.).
        this.activeIdx = -1
        this.options = Array.from(this.element.options).map((opt, index) => {
            const li = document.createElement("li")
            li.textContent = opt.text
            // Tooltip mirrors the full option text so hovering reveals
            // anything that's visually clipped by the dropdown's width.
            li.title = opt.text
            li.className = "px-4 py-2 cursor-pointer text-primary"
            li.style.cssText = "padding: 0.375rem 0.625rem; cursor: pointer; font-size: 0.8125rem; color: var(--text-primary); line-height: 1.3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
            // Italicize the placeholder so it visually distinguishes from
            // real product options.
            if (!opt.value) li.style.fontStyle = "italic"

            li.addEventListener("mouseenter", () => this.setActive(index))
            // mousedown (not click) so we fire before the input loses focus
            // and the document-level close handler hides the menu.
            li.addEventListener("mousedown", (e) => {
                e.preventDefault()
                this.select(opt.value, opt.text)
            })

            li.dataset.value = opt.value
            li.dataset.index = index

            this.results.appendChild(li)
            return { element: li, text: opt.text.toLowerCase(), original: opt, index }
        })

        this.wrapper.appendChild(this.input)
        // results lives on <body> so it isn't clipped by any container.
        document.body.appendChild(this.results)

        // Insert after the select
        this.element.parentNode.insertBefore(this.wrapper, this.element.nextSibling)
    }

    filter() {
        // Tokenize on whitespace so a multi-word query matches options
        // where the words appear in any order, separated by anything.
        // Before this: typing "pu black" required the literal substring
        // "pu black" in the option text, so "PU - Standard PU - Black"
        // (with " - Standard PU - " between) was excluded. Now every
        // token just has to appear somewhere in the text.
        const query  = this.input.value.toLowerCase().trim()
        const tokens = query.split(/\s+/).filter(t => t.length > 0)
        let firstVisible = -1

        this.options.forEach((opt, i) => {
            const match = tokens.length === 0 || tokens.every(t => opt.text.includes(t))
            opt.element.style.display = match ? "" : "none"
            if (match && firstVisible === -1) firstVisible = i
        })

        this.open()
        // Pre-highlight the first match so Enter immediately commits it.
        this.setActive(firstVisible)
    }

    open() {
        this.positionResults()
        this.results.classList.remove("hidden")
        // Re-position on scroll/resize while open. `true` capture so
        // we catch scrolls inside nested overflow containers, not just
        // the window. Removed on close.
        if (!this._boundReposition) {
            this._boundReposition = () => this.positionResults()
            window.addEventListener("scroll", this._boundReposition, true)
            window.addEventListener("resize", this._boundReposition)
        }
    }

    close(event) {
        // Clicks on the input/select wrapper OR on a result row are
        // "inside" — don't close. Everything else closes.
        if (event && (this.wrapper.contains(event.target) || this.element.contains(event.target) || this.results.contains(event.target))) return
        this.results.classList.add("hidden")
        if (this._boundReposition) {
            window.removeEventListener("scroll", this._boundReposition, true)
            window.removeEventListener("resize", this._boundReposition)
            this._boundReposition = null
        }
    }

    // Pins the floating results dropdown directly under the input,
    // matching its width. Called on open and any subsequent scroll /
    // resize so the dropdown stays anchored as the page moves.
    positionResults() {
        const rect = this.input.getBoundingClientRect()
        this.results.style.top   = `${rect.bottom + 2}px`
        this.results.style.left  = `${rect.left}px`
        this.results.style.width = `${rect.width}px`
    }

    select(value, text) {
        this.element.value = value
        this.input.value = text
        // Keep the hover-tooltip in sync with whatever's now selected.
        this.input.title = text
        this.results.classList.add("hidden")
        // Trigger change event on original select for any other listeners
        this.element.dispatchEvent(new Event("change"))
    }

    // Arrow-key navigation. Skips hidden (filtered-out) rows so the
    // user can sweep through the visible results cleanly.
    navigate(event) {
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault()
                this.open()
                this.moveActive(1)
                break
            case "ArrowUp":
                event.preventDefault()
                this.moveActive(-1)
                break
            case "Enter":
                event.preventDefault()
                if (this.activeIdx >= 0) {
                    const opt = this.options[this.activeIdx]
                    if (opt) this.select(opt.original.value, opt.original.text)
                }
                break
            case "Escape":
                event.preventDefault()
                this.results.classList.add("hidden")
                this.input.blur()
                break
            case "Tab":
                this.results.classList.add("hidden")
                break
        }
    }

    visibleOptions() {
        return this.options.filter(o => o.element.style.display !== "none")
    }

    moveActive(delta) {
        const visible = this.visibleOptions()
        if (visible.length === 0) return

        const currentVisibleIdx = visible.findIndex(o => o.index === this.activeIdx)
        let nextVisibleIdx = currentVisibleIdx + delta
        // Wrap around top/bottom
        if (nextVisibleIdx < 0) nextVisibleIdx = visible.length - 1
        if (nextVisibleIdx >= visible.length) nextVisibleIdx = 0

        const target = visible[nextVisibleIdx]
        this.setActive(target.index)
        target.element.scrollIntoView({ block: "nearest" })
    }

    setActive(index) {
        this.activeIdx = index
        this.options.forEach((opt, i) => {
            if (i === index) {
                // Strong indigo highlight + explicit white text so the
                // active row is readable in both light AND dark themes.
                // The previous --color-primary-50 tint was invisible
                // against dark-mode near-white text.
                opt.element.style.backgroundColor = "var(--color-primary-600, #4b55c8)"
                opt.element.style.color           = "#ffffff"
            } else {
                opt.element.style.backgroundColor = ""
                opt.element.style.color           = "var(--text-primary)"
            }
        })
    }
}
